import { useEffect, useRef, useState } from "react";

import { Popover } from "./Popover";

import { useAsync, useDebounced } from "../hooks/useApi";
import { api } from "../lib/api";

export function SymbolSearch({ onPick }: { onPick: (symbol: string) => void }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const boxRef = useRef<HTMLDivElement>(null);
  const debounced = useDebounced(q.trim(), 200);

  const { data, loading } = useAsync(
    (signal) => api.search(debounced, signal),
    [debounced],
    debounced.length >= 1,
  );
  const results = data?.results ?? [];

  useEffect(() => setCursor(0), [debounced]);

  const choose = (symbol: string) => {
    onPick(symbol.toUpperCase());
    setQ("");
    setOpen(false);
  };

  return (
    <div className="search" ref={boxRef}>
      <svg className="search-icon" width="13" height="13" viewBox="0 0 13 13" aria-hidden="true">
        <circle cx="5.5" cy="5.5" r="4" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <path d="M8.6 8.6 L12 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
      <input
        className="input"
        placeholder="Symbol or company"
        value={q}
        spellCheck={false}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(c + 1, results.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
          else if (e.key === "Enter") {
            // Enter on a raw ticker should work even before results land, so a
            // known symbol never waits on the directory lookup.
            const hit = results[cursor];
            if (hit) choose(hit.symbol);
            else if (q.trim()) choose(q.trim());
          } else if (e.key === "Escape") setOpen(false);
        }}
      />
      <Popover
        anchorRef={boxRef}
        open={open && debounced.length >= 1}
        onClose={() => setOpen(false)}
        width={340}
      >
        <div className="search-results">
          {loading && results.length === 0 && (
            <div className="search-row"><span className="muted">searching…</span></div>
          )}
          {!loading && results.length === 0 && (
            <div
              className="search-row cursor"
              onClick={() => choose(q.trim())}
            >
              <span className="sym">{q.trim().toUpperCase()}</span>
              <span className="nm">open anyway</span>
            </div>
          )}
          {results.map((r, i) => (
            <div
              key={r.symbol}
              className={`search-row ${i === cursor ? "cursor" : ""}`}
              onMouseEnter={() => setCursor(i)}
              onClick={() => choose(r.symbol)}
            >
              <span className="sym">{r.symbol}</span>
              <span className="nm">{r.name ?? r.exchange ?? ""}</span>
            </div>
          ))}
        </div>
      </Popover>
    </div>
  );
}
